import { checkPermission } from '@/lib/permissions/check'
import { USERS_PERMISSIONS } from '@/lib/permissions/users'
import type { AuthUser } from '@/types/auth'
import type { ChannelNavigation, ChatNavigation, NavigationChangedEvent, NavigationUser } from '@/types/navigation'

/** Key ÚNICA do retrato: o hook consulta, o socket patcha. Mora aqui (e não no
 *  hook) pra `useNavigationLive` e `useNavigationSnapshot` não se importarem em ciclo. */
export const navigationKeys = { snapshot: ['users', 'navigation'] as const }

const EXIGIDAS = [
  USERS_PERMISSIONS.NAVIGATION_READ,
  USERS_PERMISSIONS.PRESENCE_READ,
  USERS_PERMISSIONS.DIRECTORY_READ,
] as const

/**
 * Gate de UI da navegação: as TRÊS permissions. A tela junta navegação × presença
 * × identidade — sem presença mostraria offline como presente, sem directory não
 * há nome nem avatar. Faltando qualquer uma: nenhum emit, nenhuma query, nenhum avatar.
 */
export function podeVerNavegacao(user: AuthUser | null): boolean {
  if (!user) return false
  return EXIGIDAS.every((p) => checkPermission(user.permissions, p))
}

/** enter/leave de um user numa lista. Mesma referência quando nada muda. */
function aplicarNaLista(users: NavigationUser[], evt: NavigationChangedEvent): NavigationUser[] {
  const j = users.findIndex((u) => u.user_id === evt.user_id)
  if (evt.action === 'leave') return j === -1 ? users : users.filter((_, k) => k !== j)
  if (j !== -1 && users[j].entered_at === evt.at) return users
  const entrada: NavigationUser = { user_id: evt.user_id, entered_at: evt.at }
  return j === -1 ? [...users, entrada] : users.map((u, k) => (k === j ? entrada : u))
}

/** Evento de chat sobre os chats de UM canal. Contato `null` no evento não apaga o conhecido. */
function aplicarNosChats(chats: ChatNavigation[], evt: NavigationChangedEvent, protocolId: string): ChatNavigation[] {
  const i = chats.findIndex((c) => c.protocol_id === protocolId)
  if (i === -1) {
    if (evt.action === 'leave') return chats
    return [...chats, { protocol_id: protocolId, contact: evt.contact ?? null, users: aplicarNaLista([], evt) }]
  }
  const chat = chats[i]
  const users = aplicarNaLista(chat.users, evt)
  const contact = evt.action === 'enter' && evt.contact ? evt.contact : chat.contact
  if (users === chat.users && contact === chat.contact) return chats
  if (users.length === 0) return chats.filter((_, k) => k !== i)
  return chats.map((c, k) => (k === i ? { ...chat, users, contact } : c))
}

/**
 * Aplica um `navigation_changed` sobre o retrato em cache.
 * - `undefined` → `undefined`: evento nunca cria a lista.
 * - Sem `protocol_id` = canal (mexe em `users`); com = chat (mexe em `chats`, sob o
 *   canal do PROTOCOLO).
 * - `enter` em canal/chat ausente CRIA; `enter` de user presente substitui o
 *   `entered_at` (mesmo `at` → mesma referência). Nunca duplica.
 * - `leave` do último user tira o chat; canal sem `users` nem `chats` sai da lista.
 * - Nada mudou → mesma referência; canal intocado mantém a referência.
 */
export function applyNavigationChange(
  atual: ChannelNavigation[] | undefined,
  evt: NavigationChangedEvent,
): ChannelNavigation[] | undefined {
  if (!atual) return atual
  const i = atual.findIndex((c) => c.channel_id === evt.channel_id)
  if (i === -1 && evt.action === 'leave') return atual
  const canal: ChannelNavigation = i === -1 ? { channel_id: evt.channel_id, users: [], chats: [] } : atual[i]

  const protocolId = evt.protocol_id
  const users = protocolId == null ? aplicarNaLista(canal.users, evt) : canal.users
  const chats = protocolId == null ? canal.chats : aplicarNosChats(canal.chats, evt, protocolId)
  if (users === canal.users && chats === canal.chats) return atual

  if (users.length === 0 && chats.length === 0) return atual.filter((_, k) => k !== i)
  const proximo = { ...canal, users, chats }
  return i === -1 ? [...atual, proximo] : atual.map((c, k) => (k === i ? proximo : c))
}

const NENHUM_CANAL: ReadonlyMap<string, ChannelNavigation> = new Map()

/** `channel_id → canal` (users + chats). Vazio é sempre o MESMO mapa (dep estável de memo). */
export function indexarPorCanal(canais: readonly ChannelNavigation[]): ReadonlyMap<string, ChannelNavigation> {
  if (canais.length === 0) return NENHUM_CANAL
  return new Map<string, ChannelNavigation>(canais.map((c) => [c.channel_id, c]))
}

const NINGUEM: readonly NavigationUser[] = []
const NINGUEM_EM_CHAT: ReadonlySet<string> = new Set()

/**
 * Quem está NO canal: quem o tem aberto ∪ quem está num chat dele (chat de B aberto
 * pela URL de A conta em B). Distinct por user, `entered_at` = o menor. `emChat` =
 * quem está em algum chat deste canal. Sem chats → os MESMOS `users`.
 */
export function usuariosDoCanal(canal: ChannelNavigation | undefined): {
  usuarios: readonly NavigationUser[]
  emChat: ReadonlySet<string>
} {
  if (!canal) return { usuarios: NINGUEM, emChat: NINGUEM_EM_CHAT }
  if (canal.chats.length === 0) return { usuarios: canal.users, emChat: NINGUEM_EM_CHAT }
  const menor = new Map<string, number>(canal.users.map((u) => [u.user_id, u.entered_at]))
  const emChat = new Set<string>()
  for (const chat of canal.chats) {
    for (const u of chat.users) {
      emChat.add(u.user_id)
      const atual = menor.get(u.user_id)
      if (atual === undefined || u.entered_at < atual) menor.set(u.user_id, u.entered_at)
    }
  }
  return { usuarios: [...menor].map(([user_id, entered_at]) => ({ user_id, entered_at })), emChat }
}

/**
 * Quem está com o chat `protocolId` aberto, em QUALQUER canal do retrato: o back o
 * entrega sob o canal do protocolo, que pode não ser o da URL. No transitório do
 * "movido" o mesmo protocolo aparece sob dois canais → une, distinct, menor
 * `entered_at`. Um canal só → os MESMOS `users`; nenhum → a MESMA lista vazia.
 */
export function usuariosDoChat(canais: readonly ChannelNavigation[], protocolId: string): readonly NavigationUser[] {
  const achados: NavigationUser[][] = []
  for (const canal of canais) {
    const chat = canal.chats.find((c) => c.protocol_id === protocolId)
    if (chat) achados.push(chat.users)
  }
  if (achados.length === 0) return NINGUEM
  if (achados.length === 1) return achados[0]
  const menor = new Map<string, number>()
  for (const u of achados.flat()) {
    const atual = menor.get(u.user_id)
    if (atual === undefined || u.entered_at < atual) menor.set(u.user_id, u.entered_at)
  }
  return [...menor].map(([user_id, entered_at]) => ({ user_id, entered_at }))
}

const NENHUM_USUARIO: ReadonlyMap<string, readonly string[]> = new Map()

/**
 * `user_id → nomes dos canais abertos`, em ordem alfabética. Canal fora do
 * catálogo é OMITIDO (sem nome não há o que mostrar). Sem canais → o MESMO mapa vazio.
 */
export function canaisPorUsuario(
  canais: readonly ChannelNavigation[],
  catalogo: readonly { db_id: string; name: string }[],
): ReadonlyMap<string, readonly string[]> {
  if (canais.length === 0) return NENHUM_USUARIO
  const nomes = new Map<string, string>(catalogo.map((c) => [c.db_id, c.name]))
  const porUsuario = new Map<string, string[]>()
  for (const canal of canais) {
    const nome = nomes.get(canal.channel_id)
    if (nome === undefined) continue
    for (const u of canal.users) {
      const lista = porUsuario.get(u.user_id)
      if (lista) lista.push(nome)
      else porUsuario.set(u.user_id, [nome])
    }
  }
  for (const lista of porUsuario.values()) lista.sort((a, b) => a.localeCompare(b))
  return porUsuario
}

/** Um chat aberto por alguém: nomes prontos pra tela. */
export interface ChatDoUsuario {
  /** `null` = canal fora do catálogo. */
  canal: string | null
  /** `null` = leitor sem `protocols:read`. */
  contato: string | null
}

const NENHUM_CHAT: ReadonlyMap<string, readonly ChatDoUsuario[]> = new Map()

/**
 * `user_id → chats abertos` (canal e contato por nome). Canal fora do catálogo NÃO
 * omite o chat (o contato ainda diz onde a pessoa está). Sem chats → o MESMO mapa vazio.
 */
export function chatsPorUsuario(
  canais: readonly ChannelNavigation[],
  catalogo: readonly { db_id: string; name: string }[],
): ReadonlyMap<string, readonly ChatDoUsuario[]> {
  if (!canais.some((c) => c.chats.length > 0)) return NENHUM_CHAT
  const nomes = new Map<string, string>(catalogo.map((c) => [c.db_id, c.name]))
  const porUsuario = new Map<string, ChatDoUsuario[]>()
  for (const canal of canais) {
    const nome = nomes.get(canal.channel_id) ?? null
    for (const chat of canal.chats) {
      const item: ChatDoUsuario = { canal: nome, contato: chat.contact?.name ?? null }
      for (const u of chat.users) {
        const lista = porUsuario.get(u.user_id)
        if (lista) lista.push(item)
        else porUsuario.set(u.user_id, [item])
      }
    }
  }
  return porUsuario
}
